import React from 'react';

import JobCompanyLogo from '../JobCompanyLogo';
import { ReactComponent as OptionIcon } from '@repo/common/assets/icons/20px/icon_20px_option_filled.svg';
import {
  TITLE_CHIP_MAP,
  getJobOptionLabel,
  BaseButton,
  useLang,
  useT,
} from '@repo/common';

const formatDday = (deadline) => {
  if (!deadline) return '';
  const date = new Date(deadline);
  if (Number.isNaN(date.getTime())) return '';

  const today = new Date();
  const todayStart = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
  );
  const deadlineStart = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
  );
  const diffDays = Math.ceil(
    (deadlineStart.getTime() - todayStart.getTime()) / (1000 * 60 * 60 * 24),
  );

  if (diffDays > 0) return `D-${diffDays}`;
  if (diffDays === 0) return 'D-day';
  return 'Closed';
};

/**
 * @param {'default'|'admin'} [variant] - 'admin': 공고 관리 상세 미리보기(흰 배경) — 868px 폭, 로고 회색 배경,
 *   데스크탑 칩 28px·흰 칩 테두리, 회사명 strong / 지역 b14 고정
 */
const DetailViewHeader = ({ job, optionMeta, onOpenActions, variant = 'default' }) => {
  const t = useT();
  const isAdminPreview = variant === 'admin';
  const { lang } = useLang();
  const title = job?.title || '-';
  const company = job?.companyName || job?.company || '-';
  const companyLogoUrl = job?.companyLogoUrl || '';
  const location = job?.location || '-';
  const employmentType = getJobOptionLabel(
    optionMeta,
    job?.employmentTypeOptionItemId,
    job?.employmentType || '-',
    lang,
  );
  const dday = formatDday(job?.applicationDeadline);
  const titleChips = job?.titleChips || [];
  const keywords = job?.keywords || [];
  const displayKeywords = (keywords || []).filter(
    (keyword) => keyword && keyword !== employmentType,
  );
  const hasE7Support = Boolean(job?.hasE7Support);
  // 흰 배경 미리보기에서는 흰 칩이 묻히지 않게 테두리를 준다
  const whiteChipBorder = isAdminPreview ? 'tabletH:border-grayscale-gray02' : '';

  return (
    <div>
      <div className={`flex flex-col items-start pb-0 tabletH:pb-6 w-full gap-4 ${isAdminPreview ? '' : 'tabletV:w-[719px]'}`}>
        <div className='flex w-full flex-col items-start gap-3 tabletV:flex-row tabletV:gap-4'>
          <div className='flex w-full items-start justify-between tabletV:w-auto'>
            <JobCompanyLogo
              src={companyLogoUrl}
              sizeClassName='size-[82px] shrink-0 tabletV:size-[92px]'
              textClassName='typo-b12 tabletV:typo-b14'
              backgroundClassName={isAdminPreview ? 'bg-grayscale-gray01' : 'bg-grayscale-gray00'}
            />

            {/* 모바일 전용 오버플로우 메뉴 */}
            {onOpenActions && (
              <button
                type='button'
                aria-label={t('More options', '더보기')}
                onClick={onOpenActions}
                className='-mr-2 flex size-9 shrink-0 items-center justify-center rounded-full text-icon-tertiary transition-colors active:bg-grayscale-gray01 tabletV:hidden'
              >
                <OptionIcon />
              </button>
            )}
          </div>

          <div className='flex w-full min-w-0 flex-col items-start gap-1.5'>
            <div className='flex min-w-0 items-center gap-2'>
              <div className={`text-text-primary typo-b14Strong truncate ${isAdminPreview ? '' : 'tabletH:typo-b14'}`}>
                {company}
              </div>
              <div className='bg-icon-placeholder rounded shrink-0 size-[3px]' />
              <div className={`text-text-placeholder typo-b14 truncate ${isAdminPreview ? '' : 'tabletV:typo-b16'}`}>
                {location}
              </div>
            </div>

            <div className='typo-title1 tabletV:typo-h6'>{title}</div>
          </div>
        </div>

        <div className='relative flex flex-wrap items-start w-full gap-2 shrink-0 py-3 tabletV:py-0'>
          {dday && (
            <BaseButton
              type='text'
              hideIcon
              bgclass={`h-[18px] bg-purple-purple01/30 tabletH:bg-purple-purple01 px-2 py-px tabletH:py-1.5 tabletH:px-3 ${isAdminPreview ? 'tabletH:h-7' : 'tabletH:h-6'}`}
              textclass='text-purple-purple04 typo-b13Strong'
              width='w-fit'
            >
              {dday === 'Closed' ? t('Closed', '마감') : dday}
            </BaseButton>
          )}
          {titleChips.map((chip) => {
            const meta = TITLE_CHIP_MAP[chip];
            if (!meta) return null;

            return (
              <BaseButton
                key={chip}
                type='text'
                size='s'
                icon={meta.icon}
                gap='gap-0.5'
                bgclass={`h-[18px] bg-purple-purple01/30 tabletH:bg-purple-purple01 px-2 py-px ${
                  isAdminPreview
                    ? 'tabletH:h-7 tabletH:py-1.5 tabletH:pl-2 tabletH:pr-3'
                    : 'tabletH:h-6 tabletH:py-1 tabletH:pl-1 tabletH:pr-[6px]'
                }`}
                textclass='text-purple-purple04 typo-b13Strong'
              >
                {t(meta.label, meta.labelKr)}
              </BaseButton>
            );
          })}
          {hasE7Support && (
            <BaseButton
              type='text'
              hideIcon
              bgclass='px-2 py-px tabletH:px-3 tabletH:py-[6px] bg-green-light01 border-none h-[18px] tabletH:h-7'
              textclass='text-green-deep04 typo-b13Strong'
              width='97px'
            >
              {t('E-7 Support', 'E-7 지원')}
            </BaseButton>
          )}
          <BaseButton
            type='text'
            hideIcon
            bgclass={`px-2 py-px tabletH:px-3 tabletH:py-[6px] h-[18px] tabletH:h-7 bg-transparent tabletH:bg-grayscale-gray00 ${whiteChipBorder}`}
            textclass='typo-b13Strong text-text-secondary'
          >
            {employmentType}
          </BaseButton>
          {displayKeywords.map((keyword, idx) => (
            <BaseButton
              key={`${keyword}-${idx}`}
              type='text'
              hideIcon
              bgclass={`px-2 py-px tabletH:px-3 tabletH:py-[6px] h-[18px] tabletH:h-7 bg-transparent tabletH:bg-grayscale-gray00 ${whiteChipBorder}`}
              textclass='typo-b13Strong text-text-secondary'
            >
              {keyword}
            </BaseButton>
          ))}
        </div>
      </div>
      <div className='hidden border-b border-grayscale-gray02 tabletV:block' />
    </div>
  );
};

export default DetailViewHeader;
